import React from "react";

import { formatNumericCurrency } from "utils";

import { CurrencyCode } from "consts";

import { Flex, Icon, Tooltip, Typography } from "components";

type BarSegment = {
  widthPct: number;
  className: string;
  tooltipContent?: React.ReactNode;
};

type TickMark = {
  value: string;
  label?: string;
  positionPct: number;
};

type PayoutProgressBarProps = {
  title: string;
  tooltipText: React.ReactNode;
  // Formatted label shown in the floating tag above the bar
  valueTagLabel: string;
  // Colour variant of the floating tag
  valueTagVariant?: "default" | "negative";
  // Horizontal position of the tag as a percentage of the track (0 = left, 100 = right)
  valueTagPositionPct?: number;
  // Optional bonus tag shown above the bar
  bonusTag?: { label: string; positionPct: number };
  // Ordered list of bar segments rendered left-to-right
  segments: BarSegment[];
  // Optional thin vertical divider at a given position
  divider?: { positionPct: number };
  // Colour theme applied to the divider
  colorVariant?: "blue" | "green";
  // Tick-marks rendered below the track
  ticks: TickMark[];
  // Show a lock icon instead of info icon (e.g. in-verification / verified states)
  isLocked?: boolean;
  dataTestId?: string;
  isPastPoP?: boolean;
};

/** Maximum position percentage for tags to prevent overflow */
const MAX_TAG_POSITION_PCT = 85;
const RIGHT_EDGE_TICK_THRESHOLD_PCT = 99;

export const PayoutProgressBar = ({
  isPastPoP = false,
  title,
  tooltipText,
  valueTagLabel,
  valueTagVariant = "default",
  valueTagPositionPct = 0,
  bonusTag,
  segments,
  divider,
  colorVariant = "blue",
  ticks,
  isLocked = false,
  dataTestId = "payout-progress-bar",
}: PayoutProgressBarProps) => (
  <div className="ppb" data-testid={dataTestId}>
    {/*  Header  */}
    <Flex align="center" justify="space-between" className="ppb-header">
      <Flex align="center" gap="0.25rem">
        <Typography variant="paragraph-m" bold>
          {title}
        </Typography>
        <Tooltip placement="top" title={tooltipText}>
          <Icon iconName="tabler/info-circle" size="s" className="ppb-info-icon" />
        </Tooltip>
      </Flex>
      {isLocked && <Icon iconName="tabler/lock" size="s" className="ppb-lock-icon" />}
    </Flex>

    {/* Floating value tag (positioned at actualPct) */}
    <div className="ppb-tag-rail">
      <div
        className={`ppb-tag ppb-tag--${valueTagVariant} ${isPastPoP ? "ppb-tag--past" : ""}`}
        style={{ left: `${Math.min(valueTagPositionPct, MAX_TAG_POSITION_PCT)}%` }}
        data-testid={`${dataTestId}-tag`}
      >
        <Typography variant="paragraph-m">{valueTagLabel}</Typography>
      </div>
      {bonusTag && (
        <div
          className="ppb-tag ppb-tag--bonus"
          style={{ left: `${Math.min(bonusTag.positionPct, MAX_TAG_POSITION_PCT)}%` }}
          data-testid={`${dataTestId}-bonus-tag`}
        >
          <Typography variant="paragraph-m">{bonusTag.label}</Typography>
        </div>
      )}
    </div>

    {/* Track */}
    <div className="ppb-track" data-testid={`${dataTestId}-track`}>
      {segments.map((seg, idx) => {
        if (seg.tooltipContent) {
          return (
            <div
              key={`${seg.className}-${seg.widthPct}-${idx}`}
              className={`ppb-segment ${seg.className} ppb-segment--hoverable`}
              style={{ width: `${Math.max(seg.widthPct, 0)}%` }}
              data-testid={`${dataTestId}-seg-${idx}`}
            >
              <div className="ppb-hover-tag ppb-tag ppb-tag--default">{seg.tooltipContent}</div>
            </div>
          );
        }
        return (
          <div
            key={`${seg.className}-${seg.widthPct}-${idx}`}
            className={`ppb-segment ${seg.className}`}
            style={{ width: `${Math.max(seg.widthPct, 0)}%` }}
            data-testid={`${dataTestId}-seg-${idx}`}
          />
        );
      })}
      {divider && (
        <span className={`ppb-divider ppb-divider--${colorVariant}`} style={{ left: `${divider.positionPct}%` }} data-testid={`${dataTestId}-divider`} />
      )}
    </div>

    {/* Tick marks */}
    <div className="ppb-ticks" data-testid={`${dataTestId}-ticks`}>
      {ticks.map((tick, i) => {
        const isFirstTick = i === 0;
        const isLastTick = i === ticks.length - 1;
        // Keep end-of-bar labels inside the card when a tick is effectively at 100%.
        const shouldRightAnchorTick = isLastTick && tick.positionPct >= RIGHT_EDGE_TICK_THRESHOLD_PCT;
        let tickVariantClass = "ppb-tick--middle";
        if (isFirstTick) {
          tickVariantClass = "ppb-tick--first";
        } else if (isLastTick) {
          tickVariantClass = "ppb-tick--last";
        }
        const tickClassName = `ppb-tick ${tickVariantClass}`;
        const tickStyle = shouldRightAnchorTick
          ? {
              left: "auto",
              right: 0,
              transform: "none",
              alignItems: "flex-end",
              textAlign: "right" as const,
            }
          : { left: `${tick.positionPct}%` };

        return (
          <div
            key={`${tick.positionPct}-${tick.value}-${tick.label ?? ""}`}
            className={tickClassName}
            style={tickStyle}
            data-testid={`${dataTestId}-tick-${i}`}
          >
            <Typography variant="paragraph-m" className="ppb-tick-value">
              {tick.value}
            </Typography>
            {tick.label && (
              <Typography variant="paragraph-m" className="ppb-tick-label">
                {tick.label}
              </Typography>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

type BarBuilderResult = {
  valueTagLabel: string;
  valueTagVariant: "default" | "negative";
  valueTagPositionPct: number;
  bonusTag?: { label: string; positionPct: number };
  segments: BarSegment[];
  divider?: { positionPct: number };
  ticks: TickMark[];
};

type PayoutBarConfig = {
  actual: number;
  eligible: number;
  maxValue: number | null;
  isNegative: boolean;
  isFutureYear: boolean;
  hasDataEntry: boolean;
  isPastPoP: boolean;
  minPotential?: number;
  maxPotential?: number;
  // Early data entry bonus amount (shown as second tag when > 0)
  bonusAmount?: number;
  currency?: CurrencyCode;
  t: (key: string, params?: Record<string, unknown>) => string;
};

export const buildPayoutProgressBarProps = ({
  actual,
  eligible,
  maxValue,
  isNegative,
  isFutureYear,
  hasDataEntry,
  isPastPoP,
  minPotential,
  maxPotential,
  bonusAmount,
  currency,
  t,
}: PayoutBarConfig): BarBuilderResult => {
  const fmt = (v: number) => formatNumericCurrency(v, { decimals: 0, currency });

  if (isPastPoP) return buildPastPayoutBar(actual, fmt);
  if (isFutureYear) return buildFuturePayoutBar(minPotential ?? 0, maxPotential ?? 0, fmt, t);
  if (isNegative) return buildNegativePayoutBar(actual, eligible, fmt, t);
  if (eligible > 0) return buildEligiblePayoutBar(actual, eligible, maxValue, bonusAmount, fmt, t);
  return buildNoEligiblePayoutBar(actual, maxValue, hasDataEntry, isPastPoP, bonusAmount, eligible, fmt, t);
};

// Negative payout (-€500 … €0 … €500 Eligible)

const buildNegativePayoutBar = (actual: number, eligible: number, fmt: (v: number) => string, t: (key: string) => string): BarBuilderResult => {
  // Fixed layout: €0 always at 50% (center), negative on left half, eligible on right half
  const dividerPct = 50;
  const solidPct = 50; // Negative segment always fills left half
  const hatchedPct = 50; // Eligible segment always fills right half

  const ticks: TickMark[] = [
    { value: fmt(0), positionPct: dividerPct }, // €0 always in the middle
  ];

  if (eligible > 0) {
    ticks.push({ value: fmt(eligible), label: t("impactPanel.payoutImpact.eligible"), positionPct: 100 });
  }

  return {
    valueTagLabel: fmt(actual),
    valueTagVariant: "negative",
    valueTagPositionPct: 0,
    segments: [
      { widthPct: solidPct, className: "ppb-segment--negative" },
      { widthPct: hatchedPct, className: "ppb-segment--hatch-blue" },
    ],
    divider: { positionPct: dividerPct },
    ticks,
  };
};

const buildEligiblePayoutBar = (
  actual: number,
  eligible: number,
  maxValue: number | null,
  bonusAmount: number | undefined,
  fmt: (v: number) => string,
  t: (key: string) => string
): BarBuilderResult => {
  // Cap actual to eligible when actual exceeds eligible
  const displayActual = Math.min(actual, eligible);

  // When both actual and maxValue are <= eligible, hide max and scale to eligible
  const shouldHideMax = maxValue !== null && maxValue <= eligible && displayActual <= eligible;

  // When both actual and maxValue exceed eligible, hide eligible tick (actual already capped above)
  const shouldCapActual = actual > eligible && maxValue !== null && maxValue > eligible;

  const effectiveMax = maxValue ?? eligible;
  const total = shouldHideMax ? Math.max(eligible, 1) : Math.max(effectiveMax, eligible, 1);

  // Calculate percentages for each segment
  const actualPct = Math.min((displayActual / total) * 100, 100);
  const eligiblePct = Math.min((eligible / total) * 100, 100);

  // Bonus percentage: extends from the divider (eligible) position
  const bonusPct = bonusAmount && bonusAmount > 0 ? Math.min((bonusAmount / total) * 100, 100 - eligiblePct) : 0;
  const bonusEndPct = Math.min(eligiblePct + bonusPct, 100);

  // Segment widths:
  const solidWidth = actualPct;
  const mutedBlueWidth = Math.max(eligiblePct - actualPct, 0);
  const bonusWidth = bonusPct;
  // Don't show hatched segment when hiding max
  const hatchBlueWidth = shouldHideMax ? 0 : Math.max(100 - bonusEndPct, 0);

  const segments: BarSegment[] = [];
  if (solidWidth > 0) {
    segments.push({ widthPct: solidWidth, className: "ppb-segment--solid-blue" });
  }
  if (mutedBlueWidth > 0) {
    segments.push({ widthPct: mutedBlueWidth, className: "ppb-segment--muted-blue" });
  }
  if (bonusWidth > 0) {
    segments.push({ widthPct: bonusWidth, className: "ppb-segment--bonus-blue" });
  }
  const hatchTooltip =
    hatchBlueWidth > 0 && maxValue !== null ? (
      <>
        <span className="ppb-hover-tag-value">{fmt(maxValue)}</span>
        <span className="ppb-hover-tag-label">{t("impactPanel.payoutImpact.nonEligible")}</span>
      </>
    ) : undefined;

  if (hatchBlueWidth > 0) {
    segments.push({ widthPct: hatchBlueWidth, className: "ppb-segment--hatch-blue", tooltipContent: hatchTooltip });
  }

  const ticks: TickMark[] = [
    { value: eligiblePct > 13 ? fmt(0) : "", positionPct: 0 }, // Show €0 at left only when it will look good visually
  ];

  if (shouldHideMax) {
    ticks.push({ value: fmt(eligible), label: t("impactPanel.payoutImpact.eligible"), positionPct: 100 });
  } else if (shouldCapActual) {
    ticks.push({ value: fmt(eligible), label: t("impactPanel.payoutImpact.eligible"), positionPct: Math.min(eligiblePct, 85) });
  } else {
    ticks.push({ value: fmt(eligible), label: t("impactPanel.payoutImpact.eligible"), positionPct: Math.min(eligiblePct, 85) });
    if (maxValue === null) {
      ticks.push({ value: "\u20AC-", label: t("impactPanel.payoutImpact.unknown"), positionPct: 100 });
    }
  }

  // Bonus tag is positioned at the end of the bonus segment
  const bonusTag = bonusAmount && bonusAmount > 0 ? { label: t("impactPanel.payoutImpact.bonus"), positionPct: Math.min(bonusEndPct, 85) } : undefined;

  // Hide divider when it would be at the end of the bar (eligible >= maxValue)
  const shouldHideDivider = shouldHideMax || eligiblePct >= 100;

  return {
    valueTagLabel: fmt(displayActual),
    valueTagVariant: "default",
    valueTagPositionPct: actualPct,
    bonusTag,
    segments,
    divider: shouldHideDivider ? undefined : { positionPct: eligiblePct },
    ticks,
  };
};

// No eligible (€0 … Not eligible / Unknown)

const buildNoEligiblePayoutBar = (
  actual: number,
  maxValue: number | null,
  hasDataEntry: boolean,
  isPastPoP: boolean,
  bonusAmount: number | undefined,
  eligiblePayout: number,
  fmt: (v: number) => string,
  t: (key: string) => string
): BarBuilderResult => {
  if (!hasDataEntry || eligiblePayout === 0) {
    //  since eligible === 0 based on the condition in buildPayoutProgressBarProps
    return {
      valueTagLabel: fmt(0),
      valueTagVariant: "default",
      valueTagPositionPct: 0,
      segments: [
        { widthPct: 0, className: "ppb-segment--solid-blue" },
        { widthPct: 100, className: "ppb-segment--muted-blue" },
      ],
      divider: { positionPct: 0 },
      ticks: [
        { value: "\u20AC-", positionPct: 0 },
        { value: "\u20AC-", label: t("impactPanel.payoutImpact.unknown"), positionPct: 100 },
      ],
    };
  }

  // Has data entry  -> show actual values with max_value
  // bonusAmount is not used here since bonus only applies when there's an eligible payout
  const effectiveMax = maxValue ?? 1;
  const total = Math.max(effectiveMax, 1);
  const actualPct = (actual / total) * 100;

  return {
    valueTagLabel: fmt(actual),
    valueTagVariant: "default",
    valueTagPositionPct: actualPct,
    segments: [
      { widthPct: Math.min(actualPct, 100) || 0, className: "ppb-segment--solid-blue" },
      { widthPct: Math.max(100 - actualPct, 0) || 100, className: "ppb-segment--muted-blue" },
    ],
    divider: { positionPct: 0 },
    ticks: [
      { value: fmt(0), positionPct: 0 },
      { value: fmt(effectiveMax), label: t("impactPanel.payoutImpact.notEligible"), positionPct: 100 },
    ],
  };
};

// Future year (€0 … Min potential … Max potential)

const buildFuturePayoutBar = (minPotential: number, maxPotential: number, fmt: (v: number) => string, t: (key: string) => string): BarBuilderResult => {
  const total = Math.max(maxPotential, 1);
  const minPct = (Math.abs(minPotential) / total) * 100;

  return {
    valueTagLabel: fmt(0),
    valueTagVariant: "default",
    valueTagPositionPct: 0,
    segments: [
      { widthPct: Math.min(minPct, 100), className: "ppb-segment--hatch-blue" },
      { widthPct: Math.max(100 - minPct, 0), className: "ppb-segment--muted-gray" },
    ],
    divider: { positionPct: Math.min(1, minPct) },
    ticks: [
      { value: minPct > 10 ? fmt(0) : "", positionPct: 0 }, // Show €0 at left only when it will look good visually
      { value: fmt(minPotential), label: t("impactPanel.payoutImpact.minPotential"), positionPct: Math.min(minPct, 50) },
      { value: fmt(maxPotential), label: t("impactPanel.payoutImpact.maxPotential"), positionPct: 100 },
    ],
  };
};

const buildPastPayoutBar = (actual: number, fmt: (v: number) => string): BarBuilderResult => ({
  valueTagLabel: fmt(actual),
  valueTagVariant: "default",
  valueTagPositionPct: 0,
  segments: [{ widthPct: 100, className: "ppb-segment--solid-blue" }],
  ticks: [{ value: "", positionPct: 0 }],
});

type Co2BarConfig = {
  actual: number;
  maxPayoutImpact: number | null;
  notEligible: number | null;
  baseline: number;
  baselineYear?: number;
  isNegative: boolean;
  isFutureYear: boolean;
  hasDataEntry: boolean;
  isPastPoP: boolean;
  t: (key: string, params?: Record<string, unknown>) => string;
};

export const buildCo2ProgressBarProps = ({
  actual,
  maxPayoutImpact,
  notEligible,
  baseline,
  baselineYear,
  isNegative,
  isFutureYear,
  hasDataEntry,
  isPastPoP,
  t,
}: Co2BarConfig): BarBuilderResult => {
  const fmtCo2 = (v: number) => {
    const rounded = Math.round(Math.abs(v));
    if (v > 0) return `+${rounded} t CO\u2082`;
    if (v < 0) return `-${rounded} t CO\u2082`;
    return `${rounded} t CO\u2082`;
  };

  if (isPastPoP) return buildPastCo2Bar(actual, fmtCo2);
  if (isFutureYear) return buildFutureCo2Bar(actual, notEligible, fmtCo2, t);
  if (isNegative) return buildNegativeCo2Bar(actual, notEligible, baseline, baselineYear, fmtCo2, t);
  return buildPositiveCo2Bar(actual, maxPayoutImpact, notEligible, hasDataEntry, isPastPoP, fmtCo2, t);
};

// Negative CO₂ (-100 t CO₂ … 50 t CO₂ 2024 Baseline … 400 t CO₂ Not eligible)

const buildNegativeCo2Bar = (
  actual: number,
  notEligible: number | null,
  baseline: number,
  baselineYear: number | undefined,
  fmtCo2: (v: number) => string,
  t: (key: string, params?: Record<string, unknown>) => string
): BarBuilderResult => {
  // Fixed layout: divider always at 50% (center), negative on left half, hatched on right half
  const dividerPct = 50;
  const solidPct = 50; // Negative segment always fills left half
  const hatchedPct = 50; // Hatched segment always fills right half

  const baselineLabel = baselineYear ? t("impactPanel.payoutImpact.baselineWithYear", { year: baselineYear }) : t("impactPanel.payoutImpact.baseline");

  // If baseline > notEligible: middle is 0, right end is baseline
  // Otherwise: middle is baseline, right end is notEligible
  const baselineExceedsNotEligible = baseline > (notEligible ?? 0);

  const ticks: TickMark[] = [];

  if (baselineExceedsNotEligible) {
    ticks.push({ value: fmtCo2(0), positionPct: dividerPct }); // 0 in the middle
    ticks.push({ value: fmtCo2(baseline), label: baselineLabel, positionPct: 100 }); // Baseline at right end
  } else {
    ticks.push({ value: fmtCo2(baseline), label: baselineLabel, positionPct: dividerPct }); // Baseline in the middle
    if (notEligible !== null) {
      ticks.push({ value: fmtCo2(notEligible), positionPct: 100 });
    }
  }

  return {
    valueTagLabel: fmtCo2(actual),
    valueTagVariant: "negative",
    valueTagPositionPct: 0,
    segments: [
      { widthPct: solidPct, className: "ppb-segment--negative" },
      { widthPct: hatchedPct, className: "ppb-segment--hatch-green" },
    ],
    divider: { positionPct: dividerPct },
    ticks,
  };
};

const buildPositiveCo2Bar = (
  actual: number,
  maxPayoutImpact: number | null,
  notEligible: number | null,
  hasDataEntry: boolean,
  isPastPoP: boolean,
  fmtCo2: (v: number) => string,
  t: (key: string) => string
): BarBuilderResult => {
  const co2Zero = "0 t CO\u2082";
  const co2Unknown = "-t CO\u2082";

  // No data entry but past PoP → Unknown state or no past PoP → empty state
  if (!hasDataEntry) {
    return {
      valueTagLabel: co2Zero,
      valueTagVariant: "default",
      valueTagPositionPct: 0,
      segments: [
        { widthPct: 0, className: "ppb-segment--solid-green" },
        { widthPct: 100, className: "ppb-segment--muted-green" },
      ],
      divider: { positionPct: 0 },
      ticks: [
        { value: co2Unknown, positionPct: 0 },
        { value: co2Unknown, label: t("impactPanel.payoutImpact.unknown"), positionPct: 100 },
      ],
    };
  }

  //  Has data entry → show actual values
  // When maxPayoutImpact >= notEligible (co2Max), don't show the co2Max segment
  const shouldHideCo2MaxSegment = maxPayoutImpact !== null && notEligible !== null && maxPayoutImpact >= notEligible;

  // Hide maxPayoutImpact when actual meets or exceeds it
  const shouldHideMaxPayoutImpact = maxPayoutImpact !== null && Math.abs(actual) >= Math.abs(maxPayoutImpact);

  // Keep notEligible as the scale even when hiding maxPayoutImpact, so actual doesn't fill the entire bar
  const effectiveMax = shouldHideCo2MaxSegment ? Math.max(maxPayoutImpact ?? 0, Math.abs(actual), 1) : Math.max(notEligible ?? 0, Math.abs(actual), 1);

  const actualPct = Math.min((Math.abs(actual) / effectiveMax) * 100, 100);
  const maxPayoutPct = maxPayoutImpact !== null ? Math.min((Math.abs(maxPayoutImpact) / effectiveMax) * 100, 100) : 0;

  const solidWidth = actualPct;
  // Don't show muted green segment when hiding maxPayoutImpact
  const mutedGreenWidth = maxPayoutImpact !== null && !shouldHideMaxPayoutImpact ? Math.max(maxPayoutPct - actualPct, 0) : 0;
  // Show hatched segment for remaining space to notEligible
  // When hiding maxPayoutImpact, hatched starts from actualPct; otherwise from max of actualPct and maxPayoutPct
  const hatchStartPct = shouldHideMaxPayoutImpact ? actualPct : Math.max(maxPayoutPct, actualPct);
  const hatchMutedWidth = shouldHideCo2MaxSegment ? 0 : Math.max(100 - hatchStartPct, 0);

  const segments: BarSegment[] = [];
  if (solidWidth > 0) {
    segments.push({ widthPct: solidWidth, className: "ppb-segment--solid-green" });
  }
  if (mutedGreenWidth > 0) {
    segments.push({ widthPct: mutedGreenWidth, className: "ppb-segment--muted-green" });
  }
  const hasAllotment = maxPayoutImpact !== null && maxPayoutImpact > 0;
  const hatchTooltip =
    hatchMutedWidth > 0 && hasAllotment && notEligible !== null ? (
      <>
        <span className="ppb-hover-tag-value">{fmtCo2(notEligible)}</span>
        <span className="ppb-hover-tag-label">{t("impactPanel.payoutImpact.nonEligible")}</span>
      </>
    ) : undefined;

  if (hatchMutedWidth > 0) {
    segments.push({ widthPct: hatchMutedWidth, className: "ppb-segment--hatch-muted", tooltipContent: hatchTooltip });
  }

  // Ensure at least one segment exists
  if (segments.length === 0) {
    segments.push({ widthPct: 100, className: "ppb-segment--muted-green" });
  }

  // Show 0 t CO₂ at the left end of the bar only when it will look good visually and when actual >= maxPayoutImpact
  const ticks: TickMark[] = [{ value: maxPayoutPct > 30 && actual >= (maxPayoutImpact ?? 0) ? co2Zero : "", positionPct: 0 }];

  // Only show maxPayoutImpact tick if actual doesn't exceed it
  if (maxPayoutImpact !== null) {
    ticks.push({
      value: fmtCo2(maxPayoutImpact),
      label: t("impactPanel.payoutImpact.maxPayoutImpact"),
      positionPct: shouldHideCo2MaxSegment ? 100 : Math.min(maxPayoutPct, 80),
    });
  }

  // When allotment > 0, non-eligible info is shown via tooltip on the hatched segment instead of a tick
  if (!shouldHideCo2MaxSegment && !hasAllotment) {
    if (notEligible !== null) {
      ticks.push({ value: fmtCo2(notEligible), label: maxPayoutPct > 80 ? "" : t("impactPanel.payoutImpact.nonEligible"), positionPct: 100 });
    } else {
      ticks.push({ value: co2Unknown, label: t("impactPanel.payoutImpact.unknown"), positionPct: 100 });
    }
  }

  // When both maxPayoutImpact and notEligible are 0, show a 0 tick at the end
  if (maxPayoutImpact === 0 && notEligible === 0) {
    ticks.push({ value: co2Zero, positionPct: 100 });
  }

  // Hide divider when maxPayoutImpact >= notEligible (co2Max) and when it would be at the end of the bar
  const shouldHideDivider = shouldHideMaxPayoutImpact && shouldHideCo2MaxSegment;

  return {
    valueTagLabel: fmtCo2(actual),
    valueTagVariant: "default",
    valueTagPositionPct: Math.min(actualPct, 85),
    segments,
    divider: maxPayoutImpact !== null && !shouldHideDivider ? { positionPct: maxPayoutPct } : undefined,
    ticks,
  };
};

// Future CO₂ (0 t CO₂ … Not eligible / Unknown)
const buildFutureCo2Bar = (actual: number, notEligible: number | null, fmtCo2: (v: number) => string, t: (key: string) => string): BarBuilderResult => {
  const total = Math.max(Math.abs(notEligible ?? 0), 1);
  const actualPct = (Math.abs(actual) / total) * 100;

  return {
    valueTagLabel: fmtCo2(actual),
    valueTagVariant: "default",
    valueTagPositionPct: Math.min(actualPct, 85),
    segments: [
      { widthPct: Math.min(actualPct, 100), className: "ppb-segment--solid-green" },
      { widthPct: Math.max(100 - actualPct, 0), className: "ppb-segment--muted-green" },
    ],
    divider: undefined,
    ticks: [
      { value: "0 t CO\u2082", positionPct: 0 },
      { value: fmtCo2(notEligible ?? 0), label: t("impactPanel.payoutImpact.notEligible"), positionPct: 100 },
    ],
  };
};

// Past CO₂ (0 t CO₂ … 50 t CO₂ 2024 Baseline)
const buildPastCo2Bar = (actual: number, fmtCo2: (v: number) => string): BarBuilderResult => ({
  valueTagLabel: fmtCo2(actual),
  valueTagVariant: "default",
  valueTagPositionPct: 0,
  segments: [{ widthPct: 100, className: "ppb-segment--solid-green" }],
  ticks: [{ value: "", positionPct: 0 }],
});
